import { ParkProcessEventsAndTClassBlocksQuery, ProcessEventsAndTClassBlocksQuery } from '../../graphql';
import { BlockService } from '../../../services/block-service/block.service';
import { capitalizeFirstLetter } from 'app/shared/helper/is-number.helper';

export class ProcessEventsAndTClassBlocksModel {
  plannedEvents: IKpiPlannedEvents[];
  tClassBlocks: IKpiTClassBlocks[];

  static mapPortfolioData(data: ProcessEventsAndTClassBlocksQuery, blockService: BlockService): ProcessEventsAndTClassBlocksModel {
    if (data) {
      const parks = data.PORTFOLIO.PARKS;
      const turbines = data.PORTFOLIO.PARKS.flatMap(p => p.TURBINES);
      const logbookData = this.groupByBlock(data.PORTFOLIO.DATA.TABLE.DATA_LGB.logbook_monthly);
      return new ProcessEventsAndTClassBlocksModel({
        plannedEvents: data.PORTFOLIO.DATA.TABLE.DATA_PLN.planned_events.map(event => {
          const currentPark = parks.find(p => p.id === event.parkId);
          const currentTurbine = turbines.find(t => t.id === event.turbineId);
          return {
            category: event.category,
            creationTs: event.creationTs,
            description: event.description,
            duration: event.duration,
            endTs: event.endTs,
            id: event.id,
            parkId: event.parkId,
            parkName: currentPark ? capitalizeFirstLetter(currentPark.name) : '-',
            productionLoss: event.productionLoss,
            revenueLoss: event.revenueLoss,
            startTs: event.startTs,
            title: event.title,
            turbineId: event.turbineId,
            turbineName: currentTurbine ? currentTurbine.code.split('_').join(' ') : '-',
            turbineSerial: currentTurbine.serialNumeric
          };
        }),
        tClassBlocks: logbookData.sort((a, b) => a.startTS - b.startTS).map(logbook => {
          const currentPark = parks.find(p => p.id === logbook.parkId);
          const currentTurbine = turbines.find(t => t.id === logbook.turbineId);
          return {
            category: logbook.category,
            tcu: ProcessEventsAndTClassBlocksModel.getTClassData(logbook.tcu, blockService),
            duration: logbook.duration,
            endTS: logbook.endTS,
            mutTS: logbook.mutTS,
            id: logbook.id,
            log: logbook.log,
            parkId: logbook.parkId,
            parkName: currentPark ? capitalizeFirstLetter(currentPark.name) : '-',
            totalProductionLoss: logbook.totalProductionLoss,
            totalRevenueLoss: logbook.totalRevenueLoss,
            startTS: logbook.startTS,
            turbineId: logbook.turbineId,
            userComment: logbook.userComment,
            turbineName: currentTurbine ? currentTurbine.code.split('_').join(' ') : '-',
            turbineSerial: currentTurbine.serialNumeric
          };
        })
      });
    } else {
      return null;
    }
  }

  static mapParkData(data: ParkProcessEventsAndTClassBlocksQuery, blockService: BlockService): ProcessEventsAndTClassBlocksModel {
    if (data) {
      const turbines = data.PARKS[0].TURBINES;
      const lobookData = this.groupByBlock(data.PARKS[0].DATA.TABLE.DATA_LGB.logbook_monthly);
      return new ProcessEventsAndTClassBlocksModel({
        plannedEvents: data.PARKS[0].DATA.TABLE.DATA_PLN.planned_events.map(event => {
          const currentTurbine = turbines.find(t => t.id === event.turbineId);
          return {
            category: event.category,
            creationTs: event.creationTs,
            description: event.description,
            duration: event.duration,
            endTs: event.endTs,
            id: event.id,
            parkId: event.parkId,
            parkName: data.PARKS[0].name,
            productionLoss: event.productionLoss,
            revenueLoss: event.revenueLoss,
            startTs: event.startTs,
            title: event.title,
            turbineId: event.turbineId,
            turbineName: currentTurbine ? currentTurbine.code.split('_').join(' ') : '-',
            turbineSerial: currentTurbine.serialNumeric
          };
        }),
        tClassBlocks: lobookData.sort((a, b) => a.startTS - b.startTS).map(logbook => {
          const currentTurbine = turbines.find(t => t.id === logbook.turbineId);
          return {
            category: logbook.category,
            tcu: ProcessEventsAndTClassBlocksModel.getTClassData(logbook.tcu, blockService),
            duration: logbook.duration,
            endTS: logbook.endTS,
            mutTS: logbook.mutTS,
            id: logbook.id,
            log: logbook.log,
            parkId: logbook.parkId,
            parkName: data.PARKS[0].name,
            totalProductionLoss: logbook.totalProductionLoss,
            totalRevenueLoss: logbook.totalRevenueLoss,
            startTS: logbook.startTS,
            turbineId: logbook.turbineId,
            userComment: logbook.userComment,
            turbineName: currentTurbine ? currentTurbine.code.split('_').join(' ') : '-',
            turbineSerial: currentTurbine.serialNumeric
          };
        })
      });
    } else {
      return null;
    }
  }

  public static getTClassData(tcu: string, blockService: BlockService): IKpiTClass {
    return {
      ...blockService.blocksHelper.tclasses.find(t => t.id === tcu),
      ...blockService.blocksHelper.blockColors.find(c => c.class === tcu.split('.')[0])
    };
  }

  private static groupByBlock(data) {
    const groupedBlocksById = [];
    const blocksIDs = [...new Set(data.map(item => item.id))];
    for (const id of blocksIDs) {
      const blocks = data.filter(b => b.id === id).sort((a, b) => a.startTS - b.startTS);
      const resultantBlock = JSON.parse(JSON.stringify(blocks[0]));
      resultantBlock.duration = 0;
      resultantBlock.totalProductionLoss = 0;
      resultantBlock.totalRevenueLoss = 0;
      blocks.forEach(b => {
        resultantBlock.duration += b.duration;
        resultantBlock.totalProductionLoss += b.totalProductionLoss;
        resultantBlock.totalRevenueLoss += b.totalRevenueLoss;
      });
      resultantBlock.endTS = blocks[blocks.length - 1].endTS;
      groupedBlocksById.push(resultantBlock);
    }
    return groupedBlocksById;
  }

  constructor(init: Partial<ProcessEventsAndTClassBlocksModel>) {
    Object.assign(this, init);
  }
}

export interface IKpiPlannedEvents {
  category?: string;
  creationTs?: number;
  description?: string;
  duration?: number;
  endTs?: number;
  id?: string;
  parkId?: string;
  parkName?: string;
  productionLoss?: number;
  revenueLoss?: number;
  startTs?: number;
  title?: string;
  turbineId?: string;
  turbineName?: string;
  userComment?: string;
  turbineSerial?: number;
}

export interface IKpiTClassBlocks {
  id?: string;
  turbineId?: string;
  turbineName?: string;
  parkId?: string;
  parkName?: string;
  tcu?: IKpiTClass;
  log?: string;
  category?: string;
  startTS?: number;
  mutTS?: number;
  endTS?: number;
  duration?: number;
  totalProductionLoss?: number;
  totalRevenueLoss?: number;
  userComment?: string;
  turbineSerial?: number;
}

export interface IKpiTClass {
  id: string;
  tcu: string;
  description: string;
  backgroundColor: string;
  highligthColor: string;
  lineColor: string;
  lineHighlightColor: string;
}
